import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, OverlayTrigger, Tooltip, Row, Col, Table } from 'react-bootstrap';
import moment from 'moment';

//components
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import { CurrentUserGroupsWidget } from '~/components/GenericForm/Widgets/UserGroupWidget';
import TimesheetOvertimeConfigurations from './TimesheetOvertimeConfigurations';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import TimePickerInput from '~/components/TimePicker';
import CustomCheckbox from '~/components/CustomCheckbox/CustomCheckbox';
import Select from '~/elements/Select';
import CustomMultiSelect from '../../../../elements/CustomMultiSelect.js';
import LoadingSpinner from '~/elements/LoadingSpinner';
import RequiredStar from '../../../../elements/RequiredStar.js';
import AlertMessage, { createAlert } from '../../../../components/Alert/AlertMessage.jsx';

//utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '../../../../utils/timeCalc.js';
import { getCompanyOvertimeTypes, translateLabels, isISOWeekend } from '~/utils/commonMethods';
import { timeFormat, totalTimeFormat } from '../../../../utils/dateFormat.js';

//assets 
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';
import Delete from '~/assets/img/deleting.svg';

const urgentCallTypes = [
    { label: 'Field Operative', value: true, title: 'Field Operative', key: "Field Operative" },
    { label: 'Customer Care', value: false, title: 'Customer Care', key: "Customer Care" },
]

const getUrgentCallDetails = (timesheetConfigs, t, overtimeTypes, dateTimeRules) => {
    return (
        <div>
            <p className='fw-bold m-0'>{t("Timesheet")} {t("Configuration")}</p>
            {timesheetConfigs?.length && timesheetConfigs.map((ot, index) => (
                <div className='d-flex justify-content-space-between font-size-12' key={index}>
                    <span className='fw-bold me-1'>{overtimeTypes[ot.overtime_code]?.name}</span>
                    <span className='fw-bold me-1'>{timeFormat(ot.from, dateTimeRules)} - {timeFormat(ot.to, dateTimeRules)}</span>
                    <span className='fw-bold'>({totalTimeFormat(ot.min_hours_in_mins, dateTimeRules)} H)</span>
                </div>
            ))}
        </div>
    )
}



class UrgentCallForm extends Component {
    constructor(props) {
        super(props);
        const { year } = props;
        this.overtimeTypes = getCompanyOvertimeTypes(props.commonStore.config.client, props.commonStore.config.overtimes, false);
        this.allEmployees = [];
        const __isFieldOperative = false;
        this.state = {
            weekData: props.urgentCallsStore.getDefaultUrgentCallWeekData(props.weekNumber, this.overtimeTypes, __isFieldOperative, year),
            isFieldOperative: __isFieldOperative,
            showTimesheetConfigModal: false,
            currentTimesheetConfig: null,
            filteredEmployees: [],
            selectedGroup: null,
            selectedUserIds: [],
            weekendDays: false,
            weekDays: false,
            doNotify: false,
            initialWeekData: props.urgentCallsStore.getDefaultUrgentCallWeekData(props.weekNumber, this.overtimeTypes, __isFieldOperative, year),
            showValidationModal: false,
            validationRows: [],
            validationHasSuccesses: false,
            isSaving: false,
            alertMessages: [],
        };
        this.handleWeekDataChange = this.handleWeekDataChange.bind(this);
        this.checkForUnsavedChanges = this.checkForUnsavedChanges.bind(this);
    }

    componentDidMount() {
        this.loadAllEmployees();
        if (this.props.userId)
            this.loadData();
    }

    loadAllEmployees = async () => {
        const { scheduleStore, userStore, usersWithUrgentCalls } = this.props;
        const { Manages = [] } = userStore.currentUser;
        const resourceResponse = await scheduleStore.fetchResourcesOnly({ pmMembers: Manages.join(',') });
        const _resources = (resourceResponse.resources || []).slice(1);
        this.allEmployees = _resources.filter(user => user.id)
            .map(user => {
                const disabled = usersWithUrgentCalls?.includes(user.id);
                return { ...user, disabled };
            });
        this.setState({ filteredEmployees: this.allEmployees });
    }

    loadData = async () => {
        const { weekNumber, userId, commonStore, urgentCallsStore, t, year } = this.props;
        try {
            const { isFieldOperative, urgentCalls } = await urgentCallsStore.loadEmployeeUrgentCalls(userId, weekNumber, year);
            const _weekData = this.state.weekData;
            urgentCalls.forEach(urgentCall => {
                const dayKey = urgentCall.for_date;
                _weekData[dayKey].isAlreadyAdded = urgentCall.id ? true : false;
                _weekData[dayKey].id = urgentCall.id || null;
                _weekData[dayKey].isChecked = true;
                _weekData[dayKey].hours = urgentCall.hours;
                _weekData[dayKey].timesheetConfigs = urgentCall.data.timesheet_configs;
                _weekData[dayKey].timesheetEntries = urgentCall.timesheet_entries;
                _weekData[dayKey].shift_details = urgentCall.data?.shift_details;
                _weekData[dayKey].isRosterRegistered = urgentCall.data?.is_roster_registered;
            })

            const _states = { weekData: _weekData, isFieldOperative: isFieldOperative, selectedUserIds: [userId] };
            if (urgentCalls.some(ele => ele?.data?.is_roster_registered)) {
                const alert = createAlert(t('Some dates cannot be edited because they are linked to standby calls generated from the roster. Please make changes in the roster.'), 'warning');
                _states.alertMessages = [...this.state.alertMessages, alert];
            }

            this.setState(_states, () => {
                this.setState({ initialWeekData: JSON.parse(JSON.stringify(_weekData)) });
            });
        }
        catch (error) {
            console.error(error);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
        }
    }

    handleTypeChange = (value) => {
        const { urgentCallsStore, commonStore, t, weekNumber, year } = this.props;
        const { weekData } = this.state;
        const newDefaults = urgentCallsStore.getDefaultUrgentCallWeekData(weekNumber, this.overtimeTypes, value, year);
        Object.entries(weekData).forEach(([date, dayData]) => {
            dayData.timesheetConfigs = newDefaults[date].timesheetConfigs;
        });
        commonStore.addNotification(t('Timesheet Overtime Configurations updated with default values, please change if required'), null, 'success', 'top-right', 3000);
        this.setState({ weekData, isFieldOperative: value });
    }

    handleWeekDataChange = (dayKey, name, value) => {
        const { weekData } = this.state;
        weekData[dayKey][name] = value;
        this.setState({ weekData: weekData },
            () => {
                if (name === 'isChecked') this.checkIfWeekorWeekendDaysSelected();
                this.checkForUnsavedChanges();
            }
        );
    }

    showValidationModal = (usersWithErrorArray, successUserIds = []) => {
        const successRows = successUserIds.map(userId => {
            const employee = this.allEmployees.find(emp => emp.id === userId);
            return { name: employee ? employee.name : userId, reason: this.props.t('Urgent work assigned successfully.'), success: true };
        });
        const errorRows = usersWithErrorArray.map(value => {
            const employee = this.allEmployees.find(emp => emp.id === value.userId);
            return { name: employee ? employee.name : value.userId, reason: value.error, success: false };
        });
        this.setState({ showValidationModal: true, validationRows: [...successRows, ...errorRows], validationHasSuccesses: successUserIds.length > 0 });
    }

    handleSave = async () => {
        const { commonStore, urgentCallsStore, t, onCloseForm, userId } = this.props;
        const { selectedUserIds, weekData, doNotify, isFieldOperative } = this.state;
        if (!Object.values(weekData).some(day => day.isChecked)) {
            return commonStore.addNotification(t('Please select at least one day to assign'), null, 'warning');
        }
        if (!isFieldOperative && Object.values(weekData).some(day => day.isChecked && day.hours === 0)) {
            return commonStore.addNotification(t('Cannot register timesheet for 0 hours'), null, 'warning');
        }
        this.setState({ isSaving: true });
        try {
            const formData = this.prepareFormData();
            if (userId) {
                await urgentCallsStore.updateUrgentCalls(userId, formData);
                commonStore.addNotification(t('Urgent work updated successfully.'), null, 'success');
                onCloseForm(true);
            } else {
                const response = await urgentCallsStore.saveUrgentCalls({
                    ...formData,
                    userIds: selectedUserIds,
                    do_notify: doNotify
                });
                if (!response?.usersWithErrorArray?.length) {
                    commonStore.addNotification(t('Urgent work assigned successfully.'), null, 'success');
                    onCloseForm(true);
                } else {
                    const successUserIds = [...new Set((response.urgentCallRecords || []).map(r => r.user_id))];
                    if (successUserIds.length) {
                        commonStore.addNotification(t('Urgent work assigned successfully.'), null, 'success');
                    }
                    this.showValidationModal(response.usersWithErrorArray, successUserIds);
                }
            }
        } catch (error) {
            console.error('Error', error);
        } finally {
            this.setState({ isSaving: false });
        }
    }

    checkForUnsavedChanges = () => {
        const { weekData, initialWeekData } = this.state;
        const { handleUnsavedChanges } = this.props;
        const hasChanges = JSON.stringify(weekData) !== JSON.stringify(initialWeekData);
        handleUnsavedChanges(hasChanges);
    }

    prepareFormData = () => {
        const { weekNumber, year } = this.props;
        const { weekData, isFieldOperative } = this.state;
        const urgentCalls = Object.values(weekData).filter(day => day.isChecked).map(day => {
            const data = day.isRosterRegistered 
                ? { timesheet_configs: day.timesheetConfigs, shift_details: day.shift_details, is_roster_registered: day.isRosterRegistered } 
                : { timesheet_configs: day.timesheetConfigs };
            return {
                for_date: day.date,
                hours: day.hours,
                data
            }
        })
        return {
            weekNumber: weekNumber,
            year: year,
            isFieldOperative: isFieldOperative,
            urgentCalls
        };
    }

    loadEmployeesForGroup = (groupId) => {
        const { commonStore, t } = this.props;
        try {
            let _filteredEmployees, _selectedUserIds = [];
            if (!groupId || groupId === -1)
                _filteredEmployees = this.allEmployees;
            else
                _filteredEmployees = (this.allEmployees).filter(emp => emp.user_groups && emp.user_groups.includes(groupId));

            if (_filteredEmployees.length) {
                _selectedUserIds = _filteredEmployees.map(emp => emp.id);
                commonStore.addNotification(t('All group employees are selected'), null, 'success');
            } else {
                commonStore.addNotification(t('There are no employees in this group'), null, 'warning');
            }

            return this.setState({ filteredEmployees: _filteredEmployees, selectedUserIds: _selectedUserIds });
        } catch (error) {
            console.error(error);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
        }

    }

    toggleTimesheetConfigModal = (dayKey = null) => {
        if (!dayKey) {
            this.setState({
                showTimesheetConfigModal: false,
                currentTimesheetConfig: null
            });
        } else {
            const { weekData } = this.state;
            const currentTimesheetConfig = {
                key: dayKey,
                timesheetConfigs: weekData[dayKey].timesheetConfigs
            }
            this.setState({
                showTimesheetConfigModal: true,
                currentTimesheetConfig
            })
        }
    }

    checkWeekDays = (name, isChecked, isWeekend) => {
        const { commonStore, t } = this.props;
        const _weekData = this.state.weekData;
        if (isWeekend) {
            Object.values(_weekData).forEach(day => {
                if (day.isWeekend && !day.isAlreadyAdded) {
                    day.isChecked = isChecked;
                }
            })
        } else {
            Object.values(_weekData).forEach(day => {
                if (!day.isWeekend && !day.isAlreadyAdded) {
                    day.isChecked = isChecked;
                }
            })
        }
        this.setState({
            weekData: _weekData,
            [name]: isChecked
        });
        if (isChecked) {
            commonStore.addNotification(t(isWeekend ? t('Weekend is selected') : t('All week days are selected')), null, 'success');
        }
    }

    checkIfWeekorWeekendDaysSelected = () => {
        const { weekData, weekendDays, weekDays } = this.state;
        const anyWeekDaysUnchecked = Object.values(weekData).filter(day => !day.isWeekend).some(day => !day.isChecked);
        const anyWeekEndDaysUnchecked = Object.values(weekData).filter(day => day.isWeekend).some(day => !day.isChecked);
        if (weekDays && anyWeekDaysUnchecked) {
            this.setState({ weekDays: false });
        }
        if (!weekDays && !anyWeekDaysUnchecked) {
            this.setState({ weekDays: true });
        }
        if (!anyWeekEndDaysUnchecked && !weekendDays) {
            this.setState({ weekendDays: true });
        }
        if (anyWeekEndDaysUnchecked && weekendDays) {
            this.setState({ weekendDays: false });
        }
    }

    removeAlert = (messages) => {
        this.setState({ alertMessages: messages });
    }

    handleUrgentWorkDelete = (id, dayKey) => {
        const { handleDelete } = this.props;
        return handleDelete(id, id => {
            return this.removeUrgentCall(id, dayKey);
        });

    }

    removeUrgentCall = async (id, dayKey) => {
        const { urgentCallsStore, commonStore, t, onCloseForm } = this.props;
        const { weekData } = this.state;
        try {
            await urgentCallsStore.deleteUrgentCall(id);
            weekData[dayKey] = {
                ...weekData[dayKey],
                isChecked: false,
                isAlreadyAdded: false,
                id: null
            };
            this.setState({ weekData });
            commonStore.addNotification(t('Deleted Successfully'), null, 'success');
            onCloseForm(true);
        }
        catch (error) {
            console.error(error);
        }
    }

    renderValidationModal() {
        const { t, onCloseForm } = this.props;
        const { validationRows, validationHasSuccesses, showValidationModal } = this.state;
        const handleClose = () => {
            this.setState({ showValidationModal: false });
            if (validationHasSuccesses) onCloseForm(true);
        };
        return (
            <Modal show={showValidationModal} onHide={handleClose} size="lg" backdrop="static" className="stacked-modal-1070-zindex" backdropClassName="stacked-modal-backdrop-1070-zindex">
                <Modal.Header>
                    <Modal.Title>{t('Urgent Work Validations')}</Modal.Title>
                    <img src={CloseModalIcon} className="cursor-pointer" onClick={handleClose} alt="close-btn" />
                </Modal.Header>
                <Modal.Body className="m-2">
                    <div className="mb-1 text-muted small">{t('Urgent work was not assigned for the following employee(s)')}.</div>
                    <div className="table-scroll">
                        <Table className="w-100">
                            <thead>
                                <tr>
                                    <th width="40%" className="text-start">{t('Employee')}</th>
                                    <th width="60%" className="text-start">{t('Reason')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {validationRows.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="timelog_array_details text-start text-capitalize">{row.name}</td>
                                        <td className={`timelog_array_details ${row.success ? 'text-success' : 'text-danger'}`}>{row.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button fill onClick={handleClose}>{t('Close')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { t, urgentCallsStore, onCloseForm, userId, commonStore, userStore } = this.props;
        const { loading } = urgentCallsStore;
        const { currentTimesheetConfig, weekData, isFieldOperative, showTimesheetConfigModal,
            filteredEmployees, selectedUserIds, selectedGroup, weekDays, weekendDays, doNotify,
            isSaving, showValidationModal, alertMessages } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();
        translateLabels(urgentCallTypes, t, 'label');
        const currentUser = userStore.currentUser;
        const isMember = currentUser?.user_type === 'member';

        if (loading)
            return <LoadingSpinner />

        return (
            <div className='m-2'>
                <AlertMessage messages={alertMessages} onClose={this.removeAlert} maxLimit={2} />
                <Row>
                    {!userId ? (
                        <><Col xs={3}>
                            <label className="control-label">{t('Group')}</label>
                            <CurrentUserGroupsWidget
                                user_id={0}
                                value={selectedGroup}
                                onChange={(groupId) => {
                                    this.setState({ selectedGroup: groupId });
                                    this.loadEmployeesForGroup(groupId);
                                }}
                                placeholder={t('Select Group')}
                                isMember={false}
                            />
                        </Col>
                            <Col xs={3}>
                                {isMember ?
                                    <>
                                        <label className="control-label">{t('User')}</label>
                                        <div className="fw-600 text-center text-capitalize color-2550AC">{currentUser.first_name} {currentUser.last_name}</div>
                                    </>
                                    :
                                    <>
                                        <label className="control-label">{t('Employees')} <RequiredStar /></label>
                                        <CustomMultiSelect
                                            options={filteredEmployees.map(emp => ({
                                                value: emp.id,
                                                label: emp.disabled ? `${emp.name} (${t("Assigned")})` : emp.name,
                                                disabled: emp.disabled,
                                                isDisabled: emp.disabled
                                            }))}
                                            value={selectedUserIds}
                                            onChange={(selectedIds) => this.setState({ selectedUserIds: selectedIds })}
                                            placeholder={t('Select employees')}
                                        />
                                    </>
                                }
                            </Col>
                            <Col xs={3}>
                                <label className="control-label">{t('Urgent work')} {('Type')} <RequiredStar /></label>
                                <Select
                                    options={urgentCallTypes}
                                    value={urgentCallTypes.find(type => type.value === isFieldOperative)}
                                    onChange={(e) => this.handleTypeChange(e.value)}
                                />
                            </Col>
                        </>
                    ) : (
                        <Col xs={9} className='d-flex justify-content-space-between align-items-center'>
                            <div className='me-4 d-flex justify-content-space-between'>
                                <span className='font-size-13 me-2'>{t('Employee')}:</span>
                                <span className='font-size-15 fw-bold'>
                                    {this.allEmployees.find(employee => employee.id === userId)?.name}
                                </span>
                            </div>
                            <div className='d-flex'>
                                <span className='font-size-13 me-2'>{t('Urgent work')} {('Type')}:</span>
                                <span className='font-size-15 fw-bold'>
                                    {urgentCallTypes.find(type => type.value === isFieldOperative)?.label}
                                </span>
                            </div>
                        </Col>
                    )}

                    <Col xs={3} className='d-flex justify-content-end align-items-end'>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip id={"urgent-call-tooltip"}>
                                    {!selectedUserIds.length ? t('Please fill all the required fields') : t('Save')}
                                </Tooltip>
                            }
                        >
                            <Button fill me2
                                disabled={loading || isSaving || !selectedUserIds.length}
                                onClick={this.handleSave}
                            >
                                {isSaving && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
                                {t("Save")}
                            </Button>
                        </OverlayTrigger>
                        <Button
                            onClick={onCloseForm}>
                            {t('Cancel')}
                        </Button>
                    </Col>
                </Row>
                <div>
                    <div className={`d-flex ${userId ? "justify-content-start" : "justify-content-space-between"} align-items-center my-2 me-2`}>
                        <div className="d-flex gap-3">
                            <CustomCheckbox
                                label={t('Weekdays')}
                                isChecked={weekDays}
                                onChange={(isChecked) => this.checkWeekDays('weekDays', isChecked, false)} />
                            <CustomCheckbox
                                label={t('Weekend')}
                                isChecked={weekendDays}
                                onChange={(isChecked) => this.checkWeekDays('weekendDays', isChecked, true)} />
                            {isFieldOperative &&
                                <span className='font-size-12 d-flex align-items-end justify-content-end text-danger'>
                                    <b>{t("Note")}:&nbsp;</b>{t("Field operatives will need to log timesheet for urgent work payments")}
                                </span>
                            }
                        </div>
                        {!isMember && !userId &&
                            <div>
                                <CustomCheckbox
                                    label={t('Notify user')}
                                    isChecked={doNotify}
                                    onChange={(isChecked) => this.setState({ doNotify: isChecked })} />
                            </div>
                        }
                    </div>
                    <div className="row g-3">
                        {Object.values(weekData).map((day) => {
                            const isHoliday = commonStore.getHolidayInfo(day.date).isHoliday;
                            const isRosterRegistered = !!day?.isRosterRegistered;
                            return (
                            <div key={day.key} className="col-12 col-md-6 col-lg-4">
                                <div className={`urgent-call-card ${isHoliday ? 'holiday' : day.isWeekend ? 'weekend' : ''}`}>
                                    <div className={`urgent-call-card-header ${isHoliday ? 'holiday' : day.isWeekend ? 'weekend' : ''}`}>
                                        <span className="fw-bold">
                                            {t(day.dayName)} ({moment(day.date).format(dateTimeRules.short_date_format)})
                                        </span>
                                        {isHoliday && <OverlayTrigger
                                            placement="right"
                                            overlay={
                                                <Tooltip id={"holiday-tooltip"}>
                                                    {commonStore.getHolidayInfo(day.date).details?.custom_name}
                                                </Tooltip>}
                                            >
                                            <i className="fa fa-info-circle ms-1 cursor-pointer" style={{ color: 'white' }}/>
                                        </OverlayTrigger>}
                                        {!day.isAlreadyAdded ?
                                            < CustomCheckbox
                                                label={t('Assign')}
                                                isChecked={day.isChecked}
                                                onChange={(e) => {
                                                    this.handleWeekDataChange(day.key, 'isChecked', e);
                                                }}
                                            />
                                            :
                                            <Button icon_sm_delete fill
                                                onClick={() => this.handleUrgentWorkDelete(day.id, day.key)}
                                                disabled={isRosterRegistered}
                                            >
                                                <img src={Delete} alt="delete" />
                                            </Button>
                                        }
                                    </div>
                                        <div className={`urgent-call-card-body ${day.isWeekend ? 'weekend' : ''}`}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div className="d-flex align-items-center">
                                                    <span className="fw-bold font-size-15">
                                                        {isRosterRegistered ? t('Standby call') : isFieldOperative ? t('Urgent Field') : t('Urgent Intake')}
                                                    </span>
                                                    <OverlayTrigger
                                                        placement="right"
                                                        overlay={
                                                            <Tooltip id={"urgent-call-tooltip"}>
                                                                {getUrgentCallDetails(day.timesheetConfigs, t, this.overtimeTypes, dateTimeRules)}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i className="fa fa-info-circle text-muted ms-1 cursor-pointer" />
                                                    </OverlayTrigger>
                                                </div>
                                                <span className={`urgent-call-status ${day.isChecked ? 'checked' : ''}`}>
                                                    {day.isChecked ? t('Assigned') : t('Unassigned')}
                                                </span>
                                            </div>

                                            {day?.shift_details &&
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <span className=" fw-bold font-size-12">{t('Shift')}</span>
                                                    <span className="font-size-12 fw-bold">
                                                        {minutesToTimeInput(day?.shift_details?.start_time)}
                                                        <span className="mx-1 text-muted">→</span>
                                                        {minutesToTimeInput(day?.shift_details?.end_time)}
                                                    </span>
                                                </div>
                                            }
                                            {!isFieldOperative &&
                                                <div className="my-2">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <CustomCheckbox
                                                            label={t('Full day')}
                                                            isChecked={day.isChecked && day.hours === 1440}
                                                            disabled={!day.isChecked || isRosterRegistered}
                                                            labelClassName="outer-boder"
                                                            onChange={(e) => this.handleWeekDataChange(day.key, 'hours', e ? 1440 : 900)}
                                                        />
                                                        {day.hours === 1440 || !day.isChecked ?
                                                            <span className="psuedo-timepicker-container">{minutesToTimeInput(day.hours)}</span>
                                                            :
                                                            <TimePickerInput
                                                                disabled={!day.isChecked || isRosterRegistered}
                                                                value={minutesToTimeInput(day.hours)}
                                                                onChange={(value) => this.handleWeekDataChange(day.key, 'hours', getMinutesFromTimeInput(value))}
                                                                format="HH:mm"
                                                                className="worklog-input"
                                                            />
                                                        }
                                                    </div>
                                                </div>
                                            }

                                            <Row className="justify-content-center align-items-center mx-1">
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip id={"urgent-call-timesheet-settings-tooltip"}>
                                                            {!day.isChecked ? t('Unassigned') : !day.timesheetEntries ? t('Edit') : t('Timesheet enties are already logged, please remove and try again!')}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Button fill
                                                        disabled={!day.isChecked || day.timesheetEntries || isRosterRegistered}
                                                        onClick={() => this.toggleTimesheetConfigModal(day.key)}
                                                    >
                                                        {t('Settings')}<i className="fas fa-cog ms-2 font-size-15" />
                                                    </Button>
                                                </OverlayTrigger>
                                            </Row>
                                        </div>
                                </div>
                            </div>
                        );
                        })}
                    </div>
                </div>
                {showValidationModal && this.renderValidationModal()}
                {showTimesheetConfigModal && (
                    <Modal
                        show={showTimesheetConfigModal}
                        onHide={() => this.toggleTimesheetConfigModal(null)}
                        size="lg"
                        backdrop="static"
                    >
                        <Modal.Header>
                            <Modal.Title>
                                {t('Timesheet Overtime Settings')}
                            </Modal.Title>
                            <img
                                src={CloseModalIcon}
                                className="cursor-pointer"
                                onClick={() => this.toggleTimesheetConfigModal(null)}
                                alt='close-btn'
                            />
                        </Modal.Header>
                        <Modal.Body style={{ minHeight: '500px' }} className='mb-4'>
                            <TimesheetOvertimeConfigurations
                                overtimeTypes={this.overtimeTypes}
                                currentTimesheetConfig={currentTimesheetConfig}
                                updateTimesheetConfigs={(dayKey, timesheetConfigs) => this.handleWeekDataChange(dayKey, 'timesheetConfigs', timesheetConfigs)}
                                isFieldOperative={isFieldOperative}
                            />
                        </Modal.Body>
                    </Modal>
                )}
            </div>
        );
    }
}

export default inject('commonStore', 'urgentCallsStore', 'scheduleStore', 'userStore')(withLocalization(withRemoveDialog(observer(UrgentCallForm))));